import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useSession } from "../auth/SessionProvider";
import { useMfaGate } from "../auth/mfa";
import { ADMIN_HOME } from "../auth/redirectTarget";
import { BoxState } from "../ui/BoxState";
import { AuthFrame } from "./auth/AuthFrame";
import { CodeStep } from "./auth/CodeStep";

/**
 * The second step of signing in: the code from the authenticator app.
 *
 * architecture.md section 4 requires `aal2` for every admin route and every admin RPC, and
 * `public.is_platform_admin()` refuses an `aal1` session even when the allow-list row is there. So
 * this screen is what stands between a correct password and Petrovic's own views. A client account
 * without a verified factor never reaches it: `useMfaGate` answers `satisfied` and the sign-in goes
 * straight to the portal.
 *
 * The three failures this screen has to name rather than swallow:
 *
 * - a wrong code, which is the ordinary one and must not read as a broken portal;
 * - an expired challenge, which happens whenever the form sits open past its window and is the
 *   failure most likely to be mistaken for a wrong code;
 * - a required factor that does not exist, which no amount of typing fixes and which the person
 *   holding the allow-list row can cause themselves.
 *
 * Every one of them ends in a sentence. None of them ends in an empty screen.
 *
 * Both ways off this screen sign out with `local` scope. Supabase's default is `global`, which
 * revokes every session the account holds anywhere, and abandoning an unfinished sign-in in one
 * browser is not a reason to end one on a phone.
 */

export function VerificationRoute() {
  const { status, signOut } = useSession();
  const gate = useMfaGate();
  const navigate = useNavigate();
  const location = useLocation();

  const factorId = gate.data?.kind === "challenge-required" ? gate.data.factorId : null;

  if (status === "signed-out") {
    return <Navigate to="/login" replace />;
  }

  if (gate.isLoading) {
    return (
      <AuthFrame>
        <BoxState kind="loading" title="Kontrollerar inloggningen" />
      </AuthFrame>
    );
  }

  if (gate.isError) {
    return (
      <AuthFrame>
        <BoxState
          kind="error"
          title="Vi kunde inte kontrollera inloggningen"
          body="Ingenting har ändrats. Försök igen."
          onRetry={() => void gate.refetch()}
        />
      </AuthFrame>
    );
  }

  // Nothing left to answer. Somebody navigated here directly, or the factor was answered in another
  // tab. Either way this screen has no job, and standing on it would look like a lockout.
  if (gate.data?.kind === "satisfied") {
    return <Navigate to={gate.data.adminRequired ? ADMIN_HOME : "/overview"} replace />;
  }

  /**
   * The failure that typing cannot fix, and the one way out of it.
   *
   * Deliberately not the same block as a wrong code. There is no field, no retry and no code to
   * enter: the account requires a factor and has none.
   *
   * Until account settings existed this screen was a dead end and said so. It is not one any more,
   * and the reason is worth stating because it looks like a contradiction: this session is `aal1`,
   * and yet it may enrol. `RequireSession` gates on the session and not on this answer, so
   * `/settings` is reachable, and GoTrue permits `mfa.enroll` at `aal1` for an account that holds no
   * verified factor. Verifying the new one is what issues the `aal2` token. The gate the admin
   * gateways enforce is untouched: nothing here grants anything, it only stops sending a person to a
   * screen that cannot help them when one that can exists.
   *
   * This is not the same as losing an authenticator. That case holds a verified factor, so
   * `challenge-required` is the answer instead and the person never sees this screen.
   * `PORTAL_LOST_FACTOR_CANNOT_BE_SELF_SERVED` is where that one is written down.
   */
  if (gate.data?.kind === "enrollment-missing") {
    // Not drawn in the client sketch of 2026-09-24, on purpose: only an account that requires a
    // factor reaches it, and that is an admin. It moves into the same box as every other page
    // outside the shell and keeps its two paragraphs, because this is the one state here that no
    // amount of typing fixes and the sentences are what say so.
    return (
      <AuthFrame titleId="verify-heading">
        <h2 id="verify-heading" className="k-auth-title">
          Kontot saknar verifieringsapp
        </h2>

        <p data-testid="mfa-enrollment-missing">
          Det här kontot kräver en verifieringsapp och har ingen registrerad. Utan den går
          adminvyerna inte att öppna, oavsett lösenord. Inloggningen som sådan fungerar, det är
          bara det andra steget som saknas.
        </p>
        <p className="k-auth-note">
          Registrera appen under Inställningar, så frågar nästa inloggning efter en kod.
        </p>

        <Link className="button is-primary is-block" to="/settings/security">
          <span>Till inställningar</span>
        </Link>

        <div className="k-auth-links">
          <button type="button" className="k-auth-link" onClick={() => void signOut("local")}>
            Logga ut
          </button>
        </div>
      </AuthFrame>
    );
  }

  if (!factorId) {
    return <Navigate to="/login" replace />;
  }

  // Build round 45b (2026-10-13): the step itself is `CodeStep`, shared with the screen that sets a
  // password after a recovery or an invitation link.
  return (
    <CodeStep
      factorId={factorId}
      hint="Lösenordet stämde. Kontot kräver ett andra steg."
      cancel={{ label: "Avbryt och logga ut", onClick: () => void signOut("local") }}
      onVerified={() => {
        // The destination is where the person was going, or Petrovic's own surface.
        const from = (location.state as { from?: string } | null)?.from;
        navigate(from ?? (gate.data?.adminRequired ? ADMIN_HOME : "/overview"), { replace: true });
      }}
    />
  );
}
